import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { PublicLayout } from "@/components/Navbar";
import { Package, RefreshCw, FileDown, Loader2 } from "lucide-react";

const sageColor = (s) => s === "SYNCED" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/40"
  : s === "REQUIRES ATTENTION" ? "bg-rose-500/15 text-rose-400 border-rose-500/40"
  : "bg-amber-500/15 text-amber-400 border-amber-500/40";

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [busy, setBusy] = useState("");
  const { refreshCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    const load = () => api.get("/retailer/orders").then(({ data }) => setOrders(data));
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  const reorder = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post(`/retailer/orders/${id}/reorder`);
      await refreshCart();
      if (data.added.length === 0) {
        toast.error("None of these items are currently available to reorder");
      } else {
        let msg = `${data.added.length} item(s) added to cart`;
        if (data.adjusted.length) msg += ` — some quantities were reduced to match availability`;
        if (data.skipped.length) msg += `, ${data.skipped.length} unavailable`;
        toast.success(msg);
        navigate("/portal/cart");
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <PublicLayout>
      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-8">
        <h1 className="font-serif-display text-4xl font-bold text-slate-50">My <span className="mc-gold-gradient">Orders</span></h1>

        {orders.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-[#2A2E39] p-12 text-center text-[#64748B]">
            <Package className="mx-auto h-10 w-10 opacity-40" />
            <p className="mt-4">No orders yet. <Link to="/portal" className="text-[#D4AF37]">Start ordering</Link></p>
          </div>
        ) : (
          <div className="mt-8 space-y-4">
            {orders.map((o) => (
              <div key={o.id} data-testid={`order-row-${o.order_number}`} className="rounded-2xl border border-[#2A2E39] bg-[#1A1D24] p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="font-mono-data text-lg font-bold text-[#D4AF37]">{o.order_number}</div>
                    <div className="text-xs text-[#64748B]">{new Date(o.created_at).toLocaleString("en-IE")}</div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full border border-[#2A2E39] px-3 py-1 text-xs font-semibold text-slate-200">{o.status}</span>
                    <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${sageColor(o.sage_status)}`}>
                      Sage: {o.sage_status}{o.sage_order_number ? ` #${o.sage_order_number}` : ""}
                    </span>
                    {o.sage_invoice_number && (
                      <span data-testid={`invoice-${o.order_number}`} className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-400">
                        Invoice {o.sage_invoice_number}
                      </span>
                    )}
                  </div>
                </div>
                <div className="mt-4 space-y-1 border-t border-[#2A2E39] pt-4">
                  {o.items.map((it) => (
                    <div key={it.sku} className="flex justify-between text-sm"><span className="text-slate-200">{it.quantity} × {it.name}</span><span className="text-slate-100">€{it.line_total.toFixed(2)}</span></div>
                  ))}
                </div>
                <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-[#2A2E39] pt-4">
                  <div className="flex flex-wrap gap-2">
                    <button data-testid={`reorder-${o.order_number}`} disabled={busy === o.id} onClick={() => reorder(o.id)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-[#D4AF37]/40 px-4 py-2 text-sm font-semibold text-[#D4AF37] transition-colors hover:bg-[#D4AF37]/10 disabled:opacity-60">
                      {busy === o.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Reorder
                    </button>
                    <a data-testid={`download-pdf-${o.order_number}`} href={`${API}/retailer/orders/${o.id}/pdf`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-[#2A2E39] px-4 py-2 text-sm font-semibold text-slate-200 transition-colors hover:border-[#D4AF37]/50 hover:text-[#D4AF37]">
                      <FileDown className="h-4 w-4" /> Download PDF
                    </a>
                  </div>
                  <div className="text-sm font-bold text-[#D4AF37]">Total €{o.total.toFixed(2)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </PublicLayout>
  );
}
